import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  ButtonBase,
  Chip,
  Collapse,
  Dialog,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Skeleton,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  BookmarkBorderRounded,
  BookmarkRounded,
  ChevronLeftRounded,
  ChevronRightRounded,
  CloseRounded,
  ContentCopyRounded,
  DownloadRounded,
  FilterListRounded,
  FullscreenRounded,
  GavelRounded,
  InsightsRounded,
  Inventory2Outlined,
  LinkRounded,
  LibraryAddRounded,
  OpenInNewRounded,
  PrintRounded,
  RestartAltRounded,
  SearchRounded,
  ShieldOutlined,
  SortRounded,
  TuneRounded,
  WarningAmberRounded,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { loadBookmarkData, loadBookmarks, updateBookmarks } from '../utils/caseExplorerStorage';
import CaseBulkImportDialog from './CaseBulkImportDialog';

const API_BASE = process.env.REACT_APP_API_BASE || '';
const LIST_WIDTH_KEY = 'lexmaple:research-list-width';

function formatNumber(value) {
  return new Intl.NumberFormat('en-CA').format(Number(value || 0));
}

function formatDate(value) {
  if (!value) return 'Date unavailable';
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: 'short', day: 'numeric' }).format(parsed);
}

function escapeHtml(value = '') {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function queryTerms(query) {
  return Array.from(new Set(String(query || '').toLowerCase().match(/[\p{L}\p{N}§'-]{2,}/gu) || [])).slice(0, 12);
}

function HighlightedText({ text, query }) {
  const terms = queryTerms(query);
  if (!terms.length) return text || '';
  const escaped = terms.map(term => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const expression = new RegExp(`(${escaped.join('|')})`, 'gi');
  return String(text || '').split(expression).map((part, index) => (
    terms.includes(part.toLowerCase())
      ? <Box component="mark" key={`${part}-${index}`} sx={{ bgcolor: theme => alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.34 : 0.2), color: 'text.primary', px: 0.15, borderRadius: 0.4 }}>{part}</Box>
      : <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
  ));
}

async function responseJson(response, fallback) {
  let body = {};
  try { body = await response.json(); } catch { /* An empty response is handled below. */ }
  if (response.ok) return body;
  if (response.status === 401) throw new Error('Your Lex Maple session expired. Sign in again, then return to Case research.');
  throw new Error(body.error || fallback);
}

function SearchResult({ decision, selected, bookmarked, query, onSelect, onBookmark }) {
  return (
    <Box
      sx={{
        position: 'relative',
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: selected ? 'action.selected' : 'transparent',
        boxShadow: selected ? 'inset 3px 0 0 #14877F' : 'none',
        '&:hover': { bgcolor: selected ? 'action.selected' : 'action.hover' },
      }}
    >
      <ButtonBase
        onClick={() => onSelect(decision)}
        aria-pressed={selected}
        sx={{ display: 'block', width: '100%', textAlign: 'left', px: 2.1, py: 1.8, pr: 6 }}
      >
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 14, lineHeight: 1.38, fontWeight: 780, color: 'text.primary' }}>
              <HighlightedText text={decision.name} query={query} />
            </Typography>
            <Typography sx={{ mt: 0.55, fontSize: 12.5, fontWeight: 760, color: 'primary.main' }}>
              <HighlightedText text={decision.citation || 'Neutral citation unavailable'} query={query} />
            </Typography>
          </Box>
          <Chip label={decision.courtCode} size="small" sx={{ height: 22, fontSize: 10, fontWeight: 760 }} />
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.7 }}>
          {decision.court} · {formatDate(decision.decisionDate)}
        </Typography>
        <Stack direction="row" spacing={0.6} sx={{ mt: 0.8 }} flexWrap="wrap" useFlexGap>
          {decision.sourceKind === 'firm-added' && <Chip size="small" label="Firm-added · review required" color="secondary" variant="outlined" sx={{ height: 21, fontSize: 10 }} />}
          {decision.analysis?.primaryCharge?.code !== 'not_classified' && <Chip size="small" label={decision.analysis.primaryCharge.label} color="primary" variant="outlined" sx={{ height: 21, fontSize: 10 }} />}
          {decision.analysis?.outcome?.code !== 'not_classified' && <Chip size="small" label={decision.analysis.outcome.label} variant="outlined" sx={{ height: 21, fontSize: 10 }} />}
          {decision.analysis?.publicationRestriction && <Chip size="small" icon={<WarningAmberRounded />} label="Publication flag" color="warning" variant="outlined" sx={{ height: 21, fontSize: 10 }} />}
        </Stack>
        <Typography color="text.secondary" sx={{ mt: 0.9, fontSize: 12.5, lineHeight: 1.55, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          <HighlightedText text={decision.excerpt || 'Open the decision to review its text.'} query={query} />
        </Typography>
      </ButtonBase>
      <Tooltip title={bookmarked ? 'Remove from saved authorities' : 'Save authority'}>
        <IconButton
          size="small"
          aria-label={bookmarked ? `Remove ${decision.name} from saved authorities` : `Save ${decision.name}`}
          onClick={event => { event.stopPropagation(); onBookmark(decision); }}
          sx={{ position: 'absolute', top: 10, right: 10 }}
        >
          {bookmarked ? <BookmarkRounded color="secondary" /> : <BookmarkBorderRounded />}
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function JudgmentSkeleton() {
  return (
    <Box sx={{ p: { xs: 2.5, lg: 5 }, maxWidth: 920, mx: 'auto', width: '100%' }}>
      <Skeleton width="65%" height={46} />
      <Skeleton width="40%" />
      <Divider sx={{ my: 3 }} />
      {[100, 92, 97, 74, 96, 88, 81].map((width, index) => <Skeleton key={`${width}-${index}`} width={`${width}%`} height={28} />)}
    </Box>
  );
}

function EmptyJudgment() {
  return (
    <Stack alignItems="center" justifyContent="center" spacing={1.5} sx={{ height: '100%', minHeight: 430, px: 3, textAlign: 'center' }}>
      <Box sx={{ width: 58, height: 58, display: 'grid', placeItems: 'center', borderRadius: 3, bgcolor: 'action.hover' }}><GavelRounded color="disabled" sx={{ fontSize: 30 }} /></Box>
      <Typography variant="h6">Choose a decision to read</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 390 }}>The complete preserved judgment, classifications, references and authoritative-source link will open here.</Typography>
    </Stack>
  );
}

function DecisionReader({
  decision,
  analysis,
  loading,
  error,
  fontSize,
  setFontSize,
  bookmarked,
  onBookmark,
  onCopyCitation,
  onCopyLink,
  onDownload,
  onPrint,
  onOpenFullScreen,
  mobile = false,
  onClose,
}) {
  const theme = useTheme();
  const judgmentRef = useRef(null);
  const matchesRef = useRef([]);
  const [findDraft, setFindDraft] = useState('');
  const [findState, setFindState] = useState({ index: -1, count: 0 });
  const [paragraph, setParagraph] = useState('');

  const selectMatch = useCallback(index => {
    const matches = matchesRef.current;
    if (!matches.length) return;
    const safeIndex = (index + matches.length) % matches.length;
    const match = matches[safeIndex];
    const range = document.createRange();
    range.setStart(match.node, match.start);
    range.setEnd(match.node, match.end);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
    match.node.parentElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setFindState({ index: safeIndex, count: matches.length });
  }, []);

  const findInDecision = useCallback(() => {
    const root = judgmentRef.current;
    const needle = findDraft.trim().toLocaleLowerCase('en-CA');
    if (!root || !needle) {
      matchesRef.current = [];
      setFindState({ index: -1, count: 0 });
      window.getSelection()?.removeAllRanges();
      return;
    }
    const matches = [];
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();
    while (node) {
      const haystack = String(node.nodeValue || '').toLocaleLowerCase('en-CA');
      let start = haystack.indexOf(needle);
      while (start >= 0) {
        matches.push({ node, start, end: start + needle.length });
        start = haystack.indexOf(needle, start + needle.length);
      }
      node = walker.nextNode();
    }
    matchesRef.current = matches;
    if (matches.length) selectMatch(0);
    else setFindState({ index: -1, count: 0 });
  }, [findDraft, selectMatch]);

  const goToParagraph = () => {
    const number = paragraph.replace(/\D/g, '');
    if (!number) return;
    judgmentRef.current?.querySelector(`#para-${number}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', bgcolor: theme.palette.mode === 'dark' ? 'background.default' : '#F7F8FA' }}>
      {(decision || mobile) && (
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} spacing={1} sx={{ px: { xs: 1.25, md: 2 }, py: 1, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
            {mobile && <IconButton aria-label="Close decision" onClick={onClose}><CloseRounded /></IconButton>}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 800 }}>{decision?.name || 'Decision'}</Typography>
              <Typography noWrap variant="caption" color="text.secondary">{decision?.citation || 'Loading complete judgment…'}</Typography>
            </Box>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.2} sx={{ flexWrap: 'wrap' }}>
            {!mobile && <Button size="small" variant="outlined" startIcon={<FullscreenRounded />} onClick={onOpenFullScreen} aria-label="Read decision full screen">Full screen</Button>}
            <Tooltip title={bookmarked ? 'Remove saved authority' : 'Save authority'}><span><IconButton size="small" disabled={!decision} onClick={onBookmark}>{bookmarked ? <BookmarkRounded color="secondary" /> : <BookmarkBorderRounded />}</IconButton></span></Tooltip>
            <Tooltip title="Copy citation"><span><IconButton size="small" disabled={!decision} onClick={() => onCopyCitation(paragraph)}><ContentCopyRounded fontSize="small" /></IconButton></span></Tooltip>
            <Tooltip title="Copy research link"><span><IconButton size="small" disabled={!decision} onClick={onCopyLink}><LinkRounded fontSize="small" /></IconButton></span></Tooltip>
            <Tooltip title="Download text"><span><IconButton size="small" disabled={!decision} onClick={onDownload}><DownloadRounded fontSize="small" /></IconButton></span></Tooltip>
            <Tooltip title="Print decision"><span><IconButton size="small" disabled={!decision} onClick={onPrint}><PrintRounded fontSize="small" /></IconButton></span></Tooltip>
            <Tooltip title="Open authoritative source"><span><IconButton component="a" size="small" disabled={!decision?.sourceUrl} href={decision?.sourceUrl || undefined} target="_blank" rel="noopener noreferrer"><OpenInNewRounded fontSize="small" /></IconButton></span></Tooltip>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            <Tooltip title="Decrease text size"><IconButton size="small" onClick={() => setFontSize(value => Math.max(13, value - 1))}><Typography fontWeight={800}>A−</Typography></IconButton></Tooltip>
            <Tooltip title="Increase text size"><IconButton size="small" onClick={() => setFontSize(value => Math.min(22, value + 1))}><Typography fontWeight={800}>A+</Typography></IconButton></Tooltip>
          </Stack>
        </Stack>
      )}

      {decision && (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ px: { xs: 1.25, md: 2 }, py: 1, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
          <TextField
            value={findDraft}
            onChange={event => setFindDraft(event.target.value)}
            onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); findInDecision(); } }}
            size="small"
            placeholder="Find in judgment"
            inputProps={{ 'aria-label': 'Find within judgment' }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchRounded fontSize="small" /></InputAdornment> }}
            sx={{ flex: 1, minWidth: 180 }}
          />
          <Stack direction="row" alignItems="center" spacing={0.25}>
            <Button size="small" onClick={findInDecision}>Find</Button>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 42, textAlign: 'center' }}>{findState.count ? `${findState.index + 1}/${findState.count}` : '0/0'}</Typography>
            <IconButton size="small" aria-label="Previous match" disabled={!findState.count} onClick={() => selectMatch(findState.index - 1)}><ChevronLeftRounded /></IconButton>
            <IconButton size="small" aria-label="Next match" disabled={!findState.count} onClick={() => selectMatch(findState.index + 1)}><ChevronRightRounded /></IconButton>
          </Stack>
          <Stack direction="row" spacing={0.5}>
            <TextField value={paragraph} onChange={event => setParagraph(event.target.value.replace(/\D/g, '').slice(0, 4))} onKeyDown={event => { if (event.key === 'Enter') goToParagraph(); }} size="small" placeholder="Para." inputProps={{ 'aria-label': 'Paragraph number', inputMode: 'numeric' }} sx={{ width: 92 }} />
            <Button size="small" variant="outlined" onClick={goToParagraph}>Go</Button>
          </Stack>
        </Stack>
      )}

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {loading && <JudgmentSkeleton />}
        {!loading && error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}
        {!loading && !error && !decision && <EmptyJudgment />}
        {!loading && !error && decision && (
          <Box sx={{ width: '100%', maxWidth: 1080, mx: 'auto', px: { xs: 1.5, sm: 2.5, md: 4 }, py: { xs: 2, md: 3 } }}>
            <Paper variant="outlined" sx={{ mb: 2, p: 1.5, borderRadius: 2.5 }}>
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                <InsightsRounded color="primary" fontSize="small" />
                <Typography sx={{ fontSize: 13, fontWeight: 800 }}>Research signals</Typography>
                {decision.sourceKind === 'firm-added' && <Chip size="small" color="secondary" variant="outlined" label="Firm-added · verify source" />}
                {analysis && <Chip size="small" label={`${Math.round(Number(analysis.confidence || 0) * 100)}% classification confidence`} variant="outlined" />}
                {(analysis?.charges?.length ? analysis.charges : analysis?.primaryCharge ? [analysis.primaryCharge] : []).slice(0, 5).map(item => <Chip size="small" key={item.code} label={item.label} color="primary" variant="outlined" />)}
                {analysis?.outcome && <Chip size="small" label={analysis.outcome.label} variant="outlined" />}
                {analysis?.stage && <Chip size="small" label={analysis.stage.label} variant="outlined" />}
                {(analysis?.issues || []).slice(0, 4).map(item => <Chip size="small" key={item.code} label={item.label} />)}
                {(analysis?.charterSections || []).map(item => <Chip size="small" key={item.code} label={item.label} color="secondary" variant="outlined" />)}
                {analysis?.publicationRestriction && <Chip size="small" icon={<WarningAmberRounded />} label="Check publication restriction" color="warning" variant="outlined" />}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>Machine-derived navigation aids—not holdings or treatment. Confirm every proposition in the reasons and note the case up before relying on it.</Typography>
            </Paper>

            <Alert severity="info" icon={<ShieldOutlined />} sx={{ mb: 2, fontSize: 12.5 }}>{decision.verificationWarning}</Alert>
            <Box
              ref={judgmentRef}
              className="archive-court-decision"
              dangerouslySetInnerHTML={{ __html: decision.documentHtml }}
              sx={{
                bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2.5,
                boxShadow: theme.palette.mode === 'dark' ? '0 16px 40px rgba(0,0,0,.2)' : '0 12px 35px rgba(18,35,52,.06)',
                px: { xs: 2, sm: 3.5, md: 6 }, py: { xs: 2.5, md: 5 }, color: 'text.primary',
                fontFamily: 'Georgia, Cambria, "Times New Roman", serif', fontSize: `${fontSize}px`, lineHeight: 1.72,
                '& h1, & h2, & h3': { fontFamily: theme.typography.fontFamily, lineHeight: 1.25 },
                '& h1': { fontSize: '1.72em', textAlign: 'center', mb: 1.5 },
                '& h2': { fontSize: '1.2em', mt: 3.5, mb: 1.5 },
                '& .court-decision-citation': { textAlign: 'center', fontWeight: 700 },
                '& .court-decision-metadata': { display: 'grid', gridTemplateColumns: 'max-content minmax(0,1fr)', columnGap: 1.5, rowGap: 0.45, maxWidth: 520, mx: 'auto', color: 'text.secondary', fontFamily: theme.typography.fontFamily, fontSize: '.82em', mb: 3.5 },
                '& .court-decision-metadata dt': { fontWeight: 750, color: 'text.primary' },
                '& .court-decision-metadata dd': { m: 0 },
                '& .numbered-paragraph': { position: 'relative', pl: { xs: '2.8em', sm: '3.7em' }, my: 1.3, scrollMarginTop: 150 },
                '& .paragraph-number': { position: 'absolute', left: 0, color: 'text.secondary', fontFamily: theme.typography.fontFamily, fontSize: '.78em', fontWeight: 650 },
                '& .court-decision-references': { mt: 5, pt: 3, borderTop: '1px solid', borderColor: 'divider', fontFamily: theme.typography.fontFamily, fontSize: '.9em' },
                '& a': { color: 'primary.main', overflowWrap: 'anywhere' },
                '& table': { width: '100%', borderCollapse: 'collapse', my: 2 },
                '& th, & td': { border: '1px solid', borderColor: 'divider', p: 1 },
              }}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={1.25} sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
              <ShieldOutlined color="action" />
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>Preserved from {decision.sourceFile || 'the supplied archive'}{decision.sourceLine ? `, line ${formatNumber(decision.sourceLine)}` : ''}. Check currency, treatment, paragraph numbering and any publication restriction at the official source.</Typography>
              <Button component="a" href={decision.sourceUrl} target="_blank" rel="noopener noreferrer" size="small" variant="outlined" endIcon={<OpenInNewRounded />}>Verify official source</Button>
            </Stack>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default function CaseArchiveReview({ embedded = false }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const desktop = useMediaQuery(theme.breakpoints.up('lg'));
  const workspaceRef = useRef(null);
  const resultsScrollRef = useRef(null);
  const [initialParams] = useState(() => new URLSearchParams(window.location.search));
  const requestedCaseRef = useRef(initialParams.get('case') || '');
  const initialPage = Math.max(1, Number(initialParams.get('page')) || 1);
  const requestedPageSize = Number(initialParams.get('pageSize'));
  const initialPageSize = [25, 50, 100].includes(requestedPageSize) ? requestedPageSize : 25;

  const [status, setStatus] = useState(null);
  const [statusError, setStatusError] = useState('');
  const [statusNonce, setStatusNonce] = useState(0);
  const [draftQuery, setDraftQuery] = useState(initialParams.get('q') || '');
  const [query, setQuery] = useState(initialParams.get('q') || '');
  const [queryMode, setQueryMode] = useState(initialParams.get('queryMode') || 'all');
  const [sort, setSort] = useState(initialParams.get('sort') || (initialParams.get('q') ? 'relevance' : 'newest'));
  const [jurisdiction, setJurisdiction] = useState(initialParams.get('jurisdiction') || '');
  const [court, setCourt] = useState(initialParams.get('court') || '');
  const [yearFrom, setYearFrom] = useState(initialParams.get('yearFrom') || '');
  const [yearTo, setYearTo] = useState(initialParams.get('yearTo') || '');
  const [charge, setCharge] = useState(initialParams.get('charge') || '');
  const [outcome, setOutcome] = useState(initialParams.get('outcome') || '');
  const [stage, setStage] = useState(initialParams.get('stage') || '');
  const [issue, setIssue] = useState(initialParams.get('issue') || '');
  const [charter, setCharter] = useState(initialParams.get('charter') || '');
  const [publication, setPublication] = useState(initialParams.get('publication') || '');
  const [filtersOpen, setFiltersOpen] = useState(Boolean(court || jurisdiction || yearFrom || yearTo || charge || outcome || stage || issue || charter || publication));
  const [page, setPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [results, setResults] = useState({ cases: [], total: 0, pageCount: 0, search: null });
  const [resultsLoading, setResultsLoading] = useState(false);
  const [resultsError, setResultsError] = useState('');
  const [resultsNonce, setResultsNonce] = useState(0);
  const [selectedSummary, setSelectedSummary] = useState(null);
  const [decision, setDecision] = useState(null);
  const [decisionAnalysis, setDecisionAnalysis] = useState(null);
  const [decisionLoading, setDecisionLoading] = useState(false);
  const [decisionError, setDecisionError] = useState('');
  const [fontSize, setFontSize] = useState(16);
  const [listWidth, setListWidth] = useState(() => {
    const stored = Number(localStorage.getItem(LIST_WIDTH_KEY));
    return stored >= 24 && stored <= 52 ? stored : 32;
  });
  const [resizing, setResizing] = useState(false);
  const [notice, setNotice] = useState('');
  const [mobileReaderOpen, setMobileReaderOpen] = useState(false);
  const [fullScreenReaderOpen, setFullScreenReaderOpen] = useState(false);
  const [bookmarks, setBookmarks] = useState(loadBookmarks);
  const [bookmarkData, setBookmarkData] = useState(loadBookmarkData);
  const [importOpen, setImportOpen] = useState(initialParams.get('import') === '1');

  useEffect(() => {
    const controller = new AbortController();
    fetch(`${API_BASE}/api/archive-cases/status`, { signal: controller.signal })
      .then(response => responseJson(response, 'Unable to read the case-library status.'))
      .then(setStatus)
      .catch(error => { if (error.name !== 'AbortError') setStatusError(error.message); });
    return () => controller.abort();
  }, [statusNonce]);

  const loadResults = useCallback(() => {
    if (!status?.available) {
      setResultsLoading(false);
      return undefined;
    }
    if (yearFrom.length === 4 && yearTo.length === 4 && Number(yearFrom) > Number(yearTo)) {
      setResultsLoading(false);
      setResultsError('The “from” year must be earlier than or equal to the “to” year.');
      return undefined;
    }
    const controller = new AbortController();
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize), queryMode, sort });
    if (query) params.set('q', query);
    if (jurisdiction) params.set('jurisdiction', jurisdiction);
    if (court) params.set('court', court);
    if (yearFrom) params.set('yearFrom', yearFrom);
    if (yearTo) params.set('yearTo', yearTo);
    if (charge) params.set('charge', charge);
    if (outcome) params.set('outcome', outcome);
    if (stage) params.set('stage', stage);
    if (issue) params.set('issue', issue);
    if (charter) params.set('charter', charter);
    if (publication) params.set('publication', publication);
    setResultsLoading(true);
    setResultsError('');
    fetch(`${API_BASE}/api/archive-cases?${params.toString()}`, { signal: controller.signal })
      .then(response => responseJson(response, 'The case search could not be completed.'))
      .then(data => {
        const cases = Array.isArray(data?.cases) ? data.cases : [];
        setResults({ ...data, cases, total: Number(data?.total || 0), pageCount: Number(data?.pageCount || 0) });
        const requestedId = requestedCaseRef.current;
        if (requestedId) {
          requestedCaseRef.current = '';
          if (!desktop) setMobileReaderOpen(true);
        }
        setSelectedSummary(previous => {
          if (requestedId) {
            return cases.find(item => item.id === requestedId) || { id: requestedId, name: initialParams.get('title') || 'Saved decision', citation: initialParams.get('citation') || '' };
          }
          return cases.find(item => item.id === previous?.id) || cases[0] || null;
        });
        resultsScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      })
      .catch(error => { if (error.name !== 'AbortError') setResultsError(error.message); })
      .finally(() => { if (!controller.signal.aborted) setResultsLoading(false); });
    return () => controller.abort();
  }, [status, page, pageSize, query, queryMode, sort, jurisdiction, court, yearFrom, yearTo, charge, outcome, stage, issue, charter, publication, initialParams, desktop]);

  useEffect(() => {
    let cancelRequest = () => {};
    const timer = window.setTimeout(() => { cancelRequest = loadResults() || (() => {}); }, resultsNonce ? 0 : 180);
    return () => { window.clearTimeout(timer); cancelRequest(); };
  }, [loadResults, resultsNonce]);

  useEffect(() => {
    const params = new URLSearchParams();
    const matterId = initialParams.get('matterId');
    if (matterId) params.set('matterId', matterId);
    params.set('view', 'decisions');
    if (query) params.set('q', query);
    if (queryMode !== 'all') params.set('queryMode', queryMode);
    if (sort !== (query ? 'relevance' : 'newest')) params.set('sort', sort);
    if (jurisdiction) params.set('jurisdiction', jurisdiction);
    if (court) params.set('court', court);
    if (yearFrom) params.set('yearFrom', yearFrom);
    if (yearTo) params.set('yearTo', yearTo);
    if (charge) params.set('charge', charge);
    if (outcome) params.set('outcome', outcome);
    if (stage) params.set('stage', stage);
    if (issue) params.set('issue', issue);
    if (charter) params.set('charter', charter);
    if (publication) params.set('publication', publication);
    if (page > 1) params.set('page', String(page));
    if (pageSize !== 25) params.set('pageSize', String(pageSize));
    if (selectedSummary?.id) {
      params.set('case', selectedSummary.id);
      if (selectedSummary.name) params.set('title', selectedSummary.name);
      if (selectedSummary.citation) params.set('citation', selectedSummary.citation);
    }
    navigate(`/research?${params.toString()}`, { replace: true });
  }, [navigate, initialParams, query, queryMode, sort, jurisdiction, court, yearFrom, yearTo, charge, outcome, stage, issue, charter, publication, page, pageSize, selectedSummary]);

  useEffect(() => {
    if (!selectedSummary?.id) {
      const timer = window.setTimeout(() => {
        setDecision(null);
        setDecisionAnalysis(null);
        setDecisionError('');
      }, 0);
      return () => window.clearTimeout(timer);
    }
    let controller;
    const timer = window.setTimeout(() => {
      controller = new AbortController();
      setDecisionLoading(true);
      setDecision(null);
      setDecisionAnalysis(null);
      setDecisionError('');
      Promise.all([
        fetch(`${API_BASE}/api/archive-cases/${encodeURIComponent(selectedSummary.id)}`, { signal: controller.signal }).then(response => responseJson(response, 'This decision could not be opened.')),
        fetch(`${API_BASE}/api/archive-cases/${encodeURIComponent(selectedSummary.id)}/analysis`, { signal: controller.signal }).then(response => response.ok ? response.json() : null),
      ])
        .then(([caseDetail, analysis]) => { setDecision(caseDetail); setDecisionAnalysis(analysis); })
        .catch(error => { if (error.name !== 'AbortError') setDecisionError(error.message); })
        .finally(() => { if (!controller.signal.aborted) setDecisionLoading(false); });
    }, 0);
    return () => { window.clearTimeout(timer); controller?.abort(); };
  }, [selectedSummary]);

  useEffect(() => {
    if (!resizing) return undefined;
    const move = event => {
      const bounds = workspaceRef.current?.getBoundingClientRect();
      if (!bounds) return;
      const percentage = ((event.clientX - bounds.left) / bounds.width) * 100;
      setListWidth(Math.max(24, Math.min(52, percentage)));
    };
    const stop = () => setResizing(false);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop, { once: true });
    return () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', stop); };
  }, [resizing]);

  useEffect(() => { if (!resizing) localStorage.setItem(LIST_WIDTH_KEY, String(listWidth)); }, [listWidth, resizing]);
  useEffect(() => { updateBookmarks(bookmarks, bookmarkData); }, [bookmarks, bookmarkData]);

  const submitSearch = event => {
    event?.preventDefault();
    setPage(1);
    const nextQuery = draftQuery.trim();
    setQuery(nextQuery);
    if (!nextQuery && sort === 'relevance') setSort('newest');
    if (nextQuery && sort === 'newest' && !query) setSort('relevance');
  };

  const resetFilters = () => {
    setDraftQuery(''); setQuery(''); setQueryMode('all'); setSort('newest'); setJurisdiction(''); setCourt('');
    setYearFrom(''); setYearTo(''); setCharge(''); setOutcome(''); setStage(''); setIssue(''); setCharter(''); setPublication(''); setPage(1);
  };

  const selectDecision = summary => {
    setSelectedSummary(summary);
    if (!desktop) setMobileReaderOpen(true);
  };

  const toggleBookmark = summary => {
    const target = decision?.id === summary?.id ? { ...summary, ...decision } : summary;
    if (!target?.id) return;
    const key = String(target.id);
    const exists = bookmarks.includes(key);
    setBookmarks(current => exists ? current.filter(id => id !== key) : [...current, key]);
    setBookmarkData(current => {
      const next = { ...current };
      if (exists) delete next[key];
      else next[key] = { ...target, source: 'hosted-case-archive', savedAt: new Date().toISOString() };
      return next;
    });
    setNotice(exists ? 'Removed from saved authorities.' : 'Saved to your authority library.');
  };

  const copyCitation = async paragraph => {
    const citation = decision?.citation || decision?.name;
    if (!citation) return;
    await navigator.clipboard.writeText(paragraph ? `${citation} at para ${paragraph}` : citation);
    setNotice(paragraph ? 'Citation and pinpoint copied.' : 'Citation copied.');
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(window.location.href);
    setNotice('Research link copied.');
  };

  const downloadDecision = () => {
    if (!decision) return;
    const content = `${decision.name}\n${decision.citation}\n${decision.court}\n${decision.documentDate}\n${decision.sourceUrl}\n\n${decision.fullText}`;
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${(decision.citation || decision.name).replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '') || 'decision'}.txt`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const printDecision = () => {
    if (!decision) return;
    const popup = window.open('', '_blank', 'noopener,noreferrer');
    if (!popup) { setNotice('Allow pop-ups to print this decision.'); return; }
    popup.document.write(`<!doctype html><html><head><title>${escapeHtml(decision.citation || decision.name)}</title><style>body{font:16px/1.65 Georgia,serif;color:#17212b;max-width:780px;margin:42px auto;padding:0 32px}h1,h2,h3{font-family:Arial,sans-serif}header{border-bottom:1px solid #bbb;margin-bottom:28px;padding-bottom:18px}.numbered-paragraph{position:relative;padding-left:3.7em;margin:0 0 1em}.paragraph-number{position:absolute;left:0;color:#5b6570;font-family:Arial,sans-serif;font-size:.78em;font-weight:600}.court-decision-references{margin-top:32px;border-top:1px solid #bbb;padding-top:20px}a{color:#0b665f}</style></head><body><header><strong>${escapeHtml(decision.court)}</strong><br>${escapeHtml(decision.citation)}</header>${decision.documentHtml}</body></html>`);
    popup.document.close(); popup.focus(); popup.print();
  };

  const activeFilters = useMemo(() => {
    const optionLabel = (collection, value) => collection?.find(item => item.code === value)?.label || value;
    return [
      jurisdiction && { key: 'jurisdiction', label: `Jurisdiction: ${{ ON: 'Ontario', BC: 'British Columbia', SCC: 'Supreme Court of Canada', OTHER: 'Other provinces and territories' }[jurisdiction]}` },
      court && { key: 'court', label: `Court: ${status?.courts?.find(item => item.code === court)?.name || court}` },
      yearFrom && { key: 'yearFrom', label: `From ${yearFrom}` },
      yearTo && { key: 'yearTo', label: `To ${yearTo}` },
      charge && { key: 'charge', label: optionLabel(status?.analysis?.chargeOptions, charge) },
      outcome && { key: 'outcome', label: optionLabel(status?.analysis?.outcomeOptions, outcome) },
      stage && { key: 'stage', label: optionLabel(status?.analysis?.stageOptions, stage) },
      issue && { key: 'issue', label: optionLabel(status?.analysis?.issueOptions, issue) },
      charter && { key: 'charter', label: optionLabel(status?.analysis?.charterOptions, charter) },
      publication && { key: 'publication', label: publication === 'flagged' ? 'Publication flag' : 'No detected publication flag' },
    ].filter(Boolean);
  }, [jurisdiction, court, yearFrom, yearTo, charge, outcome, stage, issue, charter, publication, status]);

  const clearFilter = key => {
    const setters = { jurisdiction: setJurisdiction, court: setCourt, yearFrom: setYearFrom, yearTo: setYearTo, charge: setCharge, outcome: setOutcome, stage: setStage, issue: setIssue, charter: setCharter, publication: setPublication };
    setters[key]?.('');
    setPage(1);
  };

  const start = results.total ? ((page - 1) * pageSize) + 1 : 0;
  const end = Math.min(page * pageSize, results.total);
  const yearRangeInvalid = yearFrom.length === 4 && yearTo.length === 4 && Number(yearFrom) > Number(yearTo);
  const selectedBookmarked = Boolean(selectedSummary?.id && bookmarks.includes(String(selectedSummary.id)));
  const readerProps = {
    decision,
    analysis: decisionAnalysis,
    loading: decisionLoading,
    error: decisionError,
    fontSize,
    setFontSize,
    bookmarked: selectedBookmarked,
    onBookmark: () => toggleBookmark(selectedSummary),
    onCopyCitation: copyCitation,
    onCopyLink: copyLink,
    onDownload: downloadDecision,
    onPrint: printDecision,
    onOpenFullScreen: () => setFullScreenReaderOpen(true),
  };

  return (
    <Box sx={{ height: '100%', minHeight: embedded ? 0 : 680, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: 2.5, bgcolor: 'primary.main', color: 'primary.contrastText', display: 'grid', placeItems: 'center', flexShrink: 0 }}><Inventory2Outlined /></Box>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap>
              <Typography variant="h5" sx={{ fontWeight: 820, letterSpacing: '-.025em' }}>Canadian decisions</Typography>
              {status?.available && <Chip label={`${formatNumber(status.caseCount)} searchable`} size="small" color="success" variant="outlined" />}
              {status?.copilotAccessible && <Chip label="Copilot grounded" size="small" color="secondary" variant="outlined" />}
            </Stack>
            <Typography variant="body2" color="text.secondary">Find the authority, read the complete reasons, save it, and verify the official source.</Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          <Button size="small" variant="contained" startIcon={<LibraryAddRounded />} onClick={() => setImportOpen(true)}>Add cases</Button>
          <Chip icon={<BookmarkRounded />} label={`${formatNumber(bookmarks.length)} saved`} variant="outlined" />
          {status?.yearRange?.earliest && <Chip label={`${status.yearRange.earliest}–${status.yearRange.latest}`} variant="outlined" />}
        </Stack>
      </Stack>

      {(statusError || (status && !status.available)) && (
        <Alert severity="warning" action={<Button color="inherit" size="small" onClick={() => { setStatusError(''); setStatusNonce(value => value + 1); }}>Retry</Button>}>
          {statusError || status?.message || 'The case library is being prepared.'}
        </Alert>
      )}

      <Paper component="form" onSubmit={submitSearch} variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', flexShrink: 0 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ p: 1.25 }}>
          <TextField
            value={draftQuery}
            onChange={event => setDraftQuery(event.target.value)}
            placeholder="Case name, citation, legal test, facts, or exact phrase"
            size="small"
            autoComplete="off"
            sx={{ flex: 1, minWidth: 220 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchRounded fontSize="small" /></InputAdornment> }}
            inputProps={{ 'aria-label': 'Search Canadian decisions' }}
          />
          <Button type="submit" variant="contained" startIcon={<SearchRounded />}>Search</Button>
          <Button type="button" variant={filtersOpen ? 'contained' : 'outlined'} color={filtersOpen ? 'secondary' : 'primary'} startIcon={<Badge color="warning" badgeContent={activeFilters.length}><FilterListRounded /></Badge>} onClick={() => setFiltersOpen(value => !value)}>Filters</Button>
          <FormControl size="small" sx={{ minWidth: { md: 155 } }}>
            <InputLabel>Sort</InputLabel>
            <Select value={sort} label="Sort" onChange={event => { setSort(event.target.value); setPage(1); }} startAdornment={<SortRounded sx={{ mr: 0.7, fontSize: 18, color: 'text.secondary' }} />}>
              <MenuItem value="relevance" disabled={!query}>Most relevant</MenuItem>
              <MenuItem value="newest">Newest first</MenuItem>
              <MenuItem value="oldest">Oldest first</MenuItem>
            </Select>
          </FormControl>
          {(draftQuery || query || activeFilters.length > 0) && <Tooltip title="Clear query and every filter"><IconButton type="button" onClick={resetFilters} aria-label="Clear query and filters"><RestartAltRounded /></IconButton></Tooltip>}
        </Stack>

        <Collapse in={filtersOpen}>
          <Divider />
          <Box sx={{ p: 1.5, bgcolor: 'action.hover' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,minmax(0,1fr))', lg: 'repeat(4,minmax(150px,1fr))' }, gap: 1.15 }}>
              <FormControl size="small"><InputLabel>Query match</InputLabel><Select value={queryMode} label="Query match" onChange={event => { setQueryMode(event.target.value); setPage(1); }}><MenuItem value="all">All terms</MenuItem><MenuItem value="exact">Exact phrase</MenuItem><MenuItem value="any">Any term</MenuItem></Select></FormControl>
              <FormControl size="small"><InputLabel>Jurisdiction</InputLabel><Select value={jurisdiction} label="Jurisdiction" onChange={event => { setJurisdiction(event.target.value); setCourt(''); setPage(1); }}><MenuItem value="">All Canada</MenuItem><MenuItem value="ON">Ontario</MenuItem><MenuItem value="BC">British Columbia</MenuItem><MenuItem value="SCC">Supreme Court of Canada</MenuItem><MenuItem value="OTHER">Other provinces and territories</MenuItem></Select></FormControl>
              <FormControl size="small"><InputLabel>Court</InputLabel><Select value={court} label="Court" onChange={event => { setCourt(event.target.value); setPage(1); }}><MenuItem value="">All courts</MenuItem>{(status?.courts || []).map(item => <MenuItem key={item.code} value={item.code}>{item.name} ({formatNumber(item.cases)})</MenuItem>)}</Select></FormControl>
              <Stack direction="row" spacing={1}><TextField fullWidth label="From year" value={yearFrom} onChange={event => { setYearFrom(event.target.value.replace(/\D/g, '').slice(0, 4)); setPage(1); }} size="small" inputProps={{ inputMode: 'numeric' }} /><TextField fullWidth label="To year" value={yearTo} onChange={event => { setYearTo(event.target.value.replace(/\D/g, '').slice(0, 4)); setPage(1); }} size="small" inputProps={{ inputMode: 'numeric' }} /></Stack>
              <FormControl size="small"><InputLabel>Charge / subject</InputLabel><Select value={charge} label="Charge / subject" onChange={event => { setCharge(event.target.value); setPage(1); }}><MenuItem value="">All charge families</MenuItem>{(status?.analysis?.chargeOptions || []).map(item => <MenuItem key={item.code} value={item.code}>{item.label} ({formatNumber(item.cases)})</MenuItem>)}</Select></FormControl>
              <FormControl size="small"><InputLabel>Disposition</InputLabel><Select value={outcome} label="Disposition" onChange={event => { setOutcome(event.target.value); setPage(1); }}><MenuItem value="">All dispositions</MenuItem>{(status?.analysis?.outcomeOptions || []).map(item => <MenuItem key={item.code} value={item.code}>{item.label} ({formatNumber(item.cases)})</MenuItem>)}</Select></FormControl>
              <FormControl size="small"><InputLabel>Procedural stage</InputLabel><Select value={stage} label="Procedural stage" onChange={event => { setStage(event.target.value); setPage(1); }}><MenuItem value="">All stages</MenuItem>{(status?.analysis?.stageOptions || []).map(item => <MenuItem key={item.code} value={item.code}>{item.label} ({formatNumber(item.cases)})</MenuItem>)}</Select></FormControl>
              <FormControl size="small"><InputLabel>Legal issue</InputLabel><Select value={issue} label="Legal issue" onChange={event => { setIssue(event.target.value); setPage(1); }}><MenuItem value="">All legal issues</MenuItem>{(status?.analysis?.issueOptions || []).map(item => <MenuItem key={item.code} value={item.code}>{item.label} ({formatNumber(item.cases)})</MenuItem>)}</Select></FormControl>
              <FormControl size="small"><InputLabel>Charter provision</InputLabel><Select value={charter} label="Charter provision" onChange={event => { setCharter(event.target.value); setPage(1); }}><MenuItem value="">All Charter provisions</MenuItem>{(status?.analysis?.charterOptions || []).map(item => <MenuItem key={item.code} value={item.code}>{item.label} ({formatNumber(item.cases)})</MenuItem>)}</Select></FormControl>
              <FormControl size="small"><InputLabel>Publication review</InputLabel><Select value={publication} label="Publication review" onChange={event => { setPublication(event.target.value); setPage(1); }}><MenuItem value="">All decisions</MenuItem><MenuItem value="flagged">Detected publication flag</MenuItem><MenuItem value="clear">No detected flag</MenuItem></Select></FormControl>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.1 }}>Start broad, then narrow. Quotation marks are respected; “Exact phrase” searches the complete phrase. Classifications are discovery aids and must be verified in the reasons.</Typography>
          </Box>
        </Collapse>

        {(activeFilters.length > 0 || (!query && !resultsLoading)) && (
          <Stack direction="row" spacing={0.7} alignItems="center" flexWrap="wrap" useFlexGap sx={{ px: 1.5, pb: 1.25 }}>
            {activeFilters.map(filter => <Chip key={filter.key} size="small" label={filter.label} onDelete={() => clearFilter(filter.key)} />)}
            {!query && !activeFilters.length && <><Typography variant="caption" color="text.secondary">Try:</Typography>{['R v Grant s. 24(2)', 'Jordan unreasonable delay', 'self-defence reasonable grounds'].map(example => <Chip key={example} size="small" variant="outlined" label={example} onClick={() => { setDraftQuery(example); setQuery(example); setSort('relevance'); setPage(1); }} />)}</>}
          </Stack>
        )}
      </Paper>

      {yearRangeInvalid && <Alert severity="error" sx={{ py: 0.25 }}>The “from” year must be earlier than or equal to the “to” year.</Alert>}
      {results.search?.relaxed && <Alert severity="info" sx={{ py: 0.25 }}>No decision contained every search term. Showing decisions that contain at least one term; use “Exact phrase” to require the full wording.</Alert>}

      <Paper ref={workspaceRef} variant="outlined" sx={{ flex: 1, minHeight: { xs: 520, lg: 0 }, borderRadius: 3, overflow: 'hidden', display: 'flex', flexDirection: 'row', userSelect: resizing ? 'none' : 'auto' }}>
        <Box sx={{ width: { xs: '100%', lg: `${listWidth}%` }, minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1.15, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
            <TuneRounded fontSize="small" color="action" />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 800 }}>{resultsLoading ? 'Searching decisions…' : `${formatNumber(start)}–${formatNumber(end)} of ${formatNumber(results.total)}`}</Typography>
              {!resultsLoading && query && <Typography variant="caption" color="text.secondary">Results for “{query}”</Typography>}
            </Box>
            <FormControl size="small" variant="standard"><Select value={pageSize} onChange={event => { setPageSize(Number(event.target.value)); setPage(1); }} disableUnderline inputProps={{ 'aria-label': 'Results per page' }} sx={{ fontSize: 12.5 }}>{[25, 50, 100].map(size => <MenuItem key={size} value={size}>{size} / page</MenuItem>)}</Select></FormControl>
          </Stack>

          <Box ref={resultsScrollRef} sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }} aria-live="polite">
            {resultsLoading && Array.from({ length: 6 }, (_, index) => <Box key={index} sx={{ p: 2.1, borderBottom: '1px solid', borderColor: 'divider' }}><Skeleton width="75%" /><Skeleton width="52%" /><Skeleton sx={{ mt: 1 }} /><Skeleton width="90%" /></Box>)}
            {!resultsLoading && resultsError && <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => setResultsNonce(value => value + 1)}>Retry</Button>} sx={{ m: 2 }}>{resultsError}</Alert>}
            {!resultsLoading && !resultsError && results.cases.length === 0 && <Box sx={{ px: 3, py: 7, textAlign: 'center' }}><SearchRounded color="disabled" sx={{ fontSize: 38 }} /><Typography sx={{ mt: 1, fontWeight: 750 }}>No matching decisions</Typography><Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Remove a filter, try “Any term,” or use a broader legal concept.</Typography><Button sx={{ mt: 1.5 }} onClick={resetFilters}>Clear search</Button></Box>}
            {!resultsLoading && results.cases.map(item => <SearchResult key={item.id} decision={item} selected={selectedSummary?.id === item.id} bookmarked={bookmarks.includes(String(item.id))} query={query} onSelect={selectDecision} onBookmark={toggleBookmark} />)}
          </Box>

          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ minHeight: 56, px: 1, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
            <Pagination count={Math.max(1, results.pageCount)} page={Math.min(page, Math.max(1, results.pageCount))} onChange={(_event, value) => setPage(value)} size="small" color="primary" siblingCount={0} />
          </Stack>
        </Box>

        {desktop && <Box onPointerDown={() => setResizing(true)} role="separator" aria-orientation="vertical" aria-label="Resize results and judgment panels" sx={{ width: 7, flexShrink: 0, cursor: 'col-resize', bgcolor: resizing ? 'primary.main' : 'divider', opacity: resizing ? 0.75 : 0.6, '&:hover': { bgcolor: 'primary.main', opacity: 0.7 } }} />}
        {desktop && <Box sx={{ flex: 1, minWidth: 0, minHeight: 0 }}><DecisionReader key={selectedSummary?.id || 'empty'} {...readerProps} /></Box>}
      </Paper>

      <Dialog
        fullScreen
        open={(!desktop && mobileReaderOpen) || fullScreenReaderOpen}
        onClose={() => { setMobileReaderOpen(false); setFullScreenReaderOpen(false); }}
      >
        <DecisionReader
          key={selectedSummary?.id || 'empty'}
          {...readerProps}
          mobile
          onClose={() => { setMobileReaderOpen(false); setFullScreenReaderOpen(false); }}
        />
      </Dialog>
      <CaseBulkImportDialog open={importOpen} onClose={() => setImportOpen(false)} onImported={data => { setNotice(`${formatNumber(data.imported)} case${data.imported === 1 ? '' : 's'} added to the searchable library.`); setStatusNonce(value => value + 1); setResultsNonce(value => value + 1); }} />
      <Snackbar open={Boolean(notice)} autoHideDuration={3200} onClose={() => setNotice('')} message={notice} />
    </Box>
  );
}
